/**
 * What Ask Lilia read from a photo or a paste (18 Oct): one card per formula, as the editor will
 * show it, with whether TeX printed it in this document. The author can correct the LaTeX, then
 * insert it (an equation, or inline maths at the cursor) and save it to their library.
 */
import { useEffect, useRef, useState } from "react";
import { IconCheck, IconCopy, IconBooks, IconAlertTriangle } from "@tabler/icons-react";
import i18n from "i18next";
import { api, type MathAskFormula, type MathAskProposal } from "@/lib/api";
import { renderLatexToElement } from "@/lib/katex-config";
import { useEditorStore } from "@/store/editorStore";
import { useCanEdit } from "@/lib/documentAccess";
import { cn } from "@/lib/cn";

export interface MathTurn {
  proposal: MathAskProposal;
}

/** Inserts maths in the open document: an equation after the current block, or inline at the cursor. */
export function insertMath(latex: string, display: boolean): boolean {
  const editor = useEditorStore.getState().editorInstance;
  if (!editor || editor.isDestroyed) return false;
  const source = latex.trim();
  if (!source) return false;
  if (display) {
    const { $to } = editor.state.selection;
    const at = $to.depth >= 1 ? $to.after(1) : editor.state.doc.content.size;
    return editor.chain().insertContentAt(at, { type: "equationBlock", attrs: { latex: source, equationMode: "display" } }).run();
  }
  return editor.chain().focus().insertContent({ type: "math", attrs: { latex: source } }).run();
}

export function MathProposalView({ turn }: { turn: MathTurn }) {
  const formulas = turn.proposal.formulas;
  if (formulas.length === 0) return null;
  return (
    <div className="mt-2 flex flex-col gap-2" data-testid="ask-math-proposal">
      {formulas.map((f, i) => <FormulaCard key={i} index={i} formula={f} />)}
    </div>
  );
}

function FormulaCard({ formula, index }: { formula: MathAskFormula; index: number }) {
  // Insert only where there is an open document to insert into (not in the library's Ask Lilia).
  const canEdit = useCanEdit() && !!useEditorStore((s) => s.editorInstance);
  const [latex, setLatex] = useState(formula.latex);
  const [display, setDisplay] = useState(formula.display);
  const [inserted, setInserted] = useState(false);
  const [saving, setSaving] = useState<"idle" | "naming" | "saving" | "saved">("idle");
  const [name, setName] = useState(formula.name ?? "");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const edited = latex !== formula.latex;

  useEffect(() => {
    if (!previewRef.current) return;
    try { renderLatexToElement(latex, previewRef.current, { displayMode: display, throwOnError: false }); }
    catch { previewRef.current.textContent = latex; }
  }, [latex, display]);

  const save = async () => {
    if (!name.trim()) { setSaveError(i18n.t("editor:askMath.nameNeeded")); return; }
    setSaving("saving");
    setSaveError(null);
    try {
      await api.formulas.create({ name: name.trim(), latexContent: latex.trim(), category: "math", tags: ["from-photo"] });
      setSaving("saved");
    } catch (e) {
      setSaving("naming");
      setSaveError(e instanceof Error ? e.message : String(e));
    }
  };

  const button = "inline-flex items-center gap-1 rounded-[8px] border border-[var(--lilia-border,#e4e9f1)] bg-[var(--lilia-surface,#fff)] px-2 py-1 text-[11.5px] text-[var(--lilia-text,#0f172a)] hover:border-[var(--lilia-primary-mild,#1C93C8)] disabled:opacity-60";

  return (
    <div
      data-testid="ask-math-formula"
      data-index={index}
      data-ok={formula.ok && !edited ? "true" : "false"}
      className="rounded-[10px] border border-[var(--lilia-border,#e4e9f1)] bg-[var(--lilia-surface,#fff)] p-2.5"
    >
      {formula.name && <div className="mb-1 text-[11.5px] font-medium text-[var(--lilia-text-secondary,#475569)]">{formula.name}</div>}
      <div ref={previewRef} data-testid="ask-math-preview" className="overflow-x-auto py-1 text-[var(--lilia-text,#0f172a)]" />
      <textarea
        data-testid="ask-math-latex"
        aria-label={i18n.t("editor:askMath.latexLabel")}
        value={latex}
        onChange={(e) => { setLatex(e.target.value); setInserted(false); }}
        rows={Math.min(5, Math.max(1, latex.split("\n").length))}
        spellCheck={false}
        className="mt-1 w-full resize-y rounded-[6px] border border-[var(--lilia-border,#e4e9f1)] bg-[var(--lilia-bg-muted,#f8fafc)] px-2 py-1 font-mono text-[11.5px] text-[var(--lilia-text,#0f172a)]"
      />
      <div data-testid="ask-math-status" className={cn("mt-1 flex items-start gap-1 text-[11.5px]",
        formula.ok && !edited ? "text-[var(--lilia-success,#107c41)]" : edited ? "text-[var(--lilia-text-muted,#56616e)]" : "text-[var(--lilia-danger,#c4314b)]")}>
        {formula.ok && !edited ? <><IconCheck size={13} className="mt-px shrink-0" /> {i18n.t("editor:askMath.prints")}</>
          : edited ? i18n.t("editor:askMath.edited")
          : <><IconAlertTriangle size={13} className="mt-px shrink-0" /> {i18n.t("editor:askMath.doesNotPrint", { error: formula.error ?? "" })}</>}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {canEdit && (
          <>
            <button type="button" data-testid="ask-math-insert" className={button} disabled={inserted}
              onClick={() => setInserted(insertMath(latex, display))}>
              {inserted ? <><IconCheck size={12} /> {i18n.t("editor:askMath.inserted")}</>
                : display ? i18n.t("editor:askMath.insertEquation") : i18n.t("editor:askMath.insertInline")}
            </button>
            <button type="button" data-testid="ask-math-toggle-display" className={button}
              onClick={() => { setDisplay((d) => !d); setInserted(false); }}>
              {display ? i18n.t("editor:askMath.asInline") : i18n.t("editor:askMath.asEquation")}
            </button>
          </>
        )}
        {saving === "saved" ? (
          <span data-testid="ask-math-saved" className="inline-flex items-center gap-1 text-[11.5px] text-[var(--lilia-success,#107c41)]">
            <IconCheck size={12} /> {i18n.t("editor:askMath.saved")}
          </span>
        ) : saving === "idle" ? (
          <button type="button" data-testid="ask-math-save" className={button} onClick={() => setSaving("naming")}>
            <IconBooks size={12} /> {i18n.t("editor:askMath.saveToLibrary")}
          </button>
        ) : null}
        <button type="button" data-testid="ask-math-copy" className={button}
          onClick={() => { void navigator.clipboard?.writeText(latex).then(() => setCopied(true)).catch(() => {}); }}>
          {copied ? <IconCheck size={12} /> : <IconCopy size={12} />} {i18n.t("editor:askMath.copy")}
        </button>
      </div>
      {(saving === "naming" || saving === "saving") && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <input
            data-testid="ask-math-save-name"
            aria-label={i18n.t("editor:askMath.nameLabel")}
            placeholder={i18n.t("editor:askMath.nameLabel")}
            value={name}
            autoFocus
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") void save(); if (e.key === "Escape") setSaving("idle"); }}
            className="min-w-0 flex-1 rounded-[6px] border border-[var(--lilia-border,#e4e9f1)] bg-[var(--lilia-surface,#fff)] px-2 py-1 text-[12px] text-[var(--lilia-text,#0f172a)]"
          />
          <button type="button" data-testid="ask-math-save-confirm" className={button} disabled={saving === "saving"} onClick={() => void save()}>
            {i18n.t("editor:askMath.save")}
          </button>
          {saveError && <div data-testid="ask-math-save-error" className="w-full text-[11.5px] text-[var(--lilia-danger,#c4314b)]">{saveError}</div>}
        </div>
      )}
    </div>
  );
}
